/** Centered loading spinner, inline or as a full-page overlay. */

import { Box, CircularProgress, Typography } from '@mui/material';
import type { ReactNode } from 'react';

type SpinnerSize = 'small' | 'medium' | 'large';

interface LoadingSpinnerProps {
  fullPage?: boolean;
  message?: string;
  size?: SpinnerSize;
}

const SIZE_MAP: Record<SpinnerSize, number> = {
  small: 24,
  medium: 40,
  large: 60,
};

const LoadingSpinner = ({
  fullPage = false,
  message = '',
  size = 'medium',
}: LoadingSpinnerProps): ReactNode => {
  const spinnerSize = SIZE_MAP[size];

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress size={spinnerSize} />
      {message && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  if (fullPage) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          zIndex: 9999,
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      {content}
    </Box>
  );
};

export default LoadingSpinner;
